// The device locks itself (UC-IDENTITY-003 A1; BR-IDENTITY-004). The server locks a session only on its next request, so a
// tablet left on a list would show it indefinitely, and offline it could never lock (Sprint 1 adversarial review, H3).
// One timer counts the device's idle minutes from the last pointer, key or touch; it is bounded by the server's own idle
// expiry so the two never disagree for long. That bound is modelled on the device's clock, not copied from the view: every
// request the server answers extends the real expiry (session middleware, touched at most a minute late), so the bound is
// the last answered request plus the idle time. The view's `idleExpiresAt` is a server-clock instant and is not compared
// with `Date.now()` — a device whose clock ran ahead would otherwise lock a fresh session the moment it loaded, unlock, and
// lock again for ever. When the app returns to the foreground the deadline is checked at once — background tabs throttle
// timers — and the session is re-validated with the server.
import type { SessionView } from "@visionx/contracts";
import { useEffect } from "react";
import { lastSuccessfulResponseAt } from "../api/client";
import { loadSession, lockScreen } from "./store";

const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel"] as const;
/** Activity is noted at most once a second; the timer reads the note when it fires, so activity never re-arms it. */
const ACTIVITY_THROTTLE_MS = 1_000;
/** `setTimeout` treats a delay past 2^31 − 1 ms as zero; nothing here approaches it (12 h ≈ 4.3 × 10^7 ms), but clamp anyway. */
const MAX_TIMEOUT_MS = 2_147_000_000;

/** The idle minutes for the session's device, as the server applies them: tablets and kiosks share the shorter one. */
export function idleMinutesOf(s: SessionView): number {
  return s.deviceKind === "Tablet" || s.deviceKind === "Kiosk" ? s.settings.sessionIdleMinutesTablet : s.settings.sessionIdleMinutesDesktop;
}

/**
 * When the screen must lock, in ms since the epoch on this device's clock: the idle time after the last local activity, but
 * never later than the server's idle expiry as this device can know it — the last answered request plus the idle time. So a
 * user who keeps working without a single request is still locked when the server would lock, and a request answered after
 * the last activity does not hold the screen open past the activity's own idle time. `lastResponseAt` is 0 before the first
 * answer, when there is no server bound to apply. The absolute end of the session is the server's alone: its 401 shows sign-in.
 */
export function lockDeadline(s: SessionView, lastActivityAt: number, lastResponseAt: number): number {
  const idleMs = idleMinutesOf(s) * 60_000;
  const serverIdle = lastResponseAt > 0 ? lastResponseAt + idleMs : Number.POSITIVE_INFINITY;
  return Math.min(lastActivityAt + idleMs, serverIdle);
}

/** Arms the idle lock for the signed-in session; nothing while there is none. Mounted once, by the shell. */
export function useIdleLock(s: SessionView | null): void {
  useEffect(() => {
    if (!s) return;
    let lastActivityAt = Date.now();
    let lastNoted = lastActivityAt;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = () => lockDeadline(s, lastActivityAt, lastSuccessfulResponseAt());

    const fire = () => {
      timer = undefined;
      if (Date.now() >= deadline()) { void lockScreen(); return; }
      arm();
    };
    const arm = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(fire, Math.min(Math.max(deadline() - Date.now(), 0), MAX_TIMEOUT_MS));
    };
    const onActivity = () => {
      const now = Date.now();
      if (now - lastNoted < ACTIVITY_THROTTLE_MS) return;
      lastNoted = now;
      lastActivityAt = now;
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() >= deadline()) { void lockScreen(); return; }
      void loadSession();
    };

    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true, capture: true });
    document.addEventListener("visibilitychange", onVisibility);
    arm();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [s]);
}
